import React, { useState, useEffect } from 'react';
import { PerformanceMonitor } from '../utils/performanceOptimizations';

interface PerformanceDashboardProps {
  isVisible: boolean;
  onToggle: () => void;
}

const PerformanceDashboard: React.FC<PerformanceDashboardProps> = ({ isVisible, onToggle }) => {
  const [metrics, setMetrics] = useState<Record<string, { average: number; count: number; max: number }>>({});
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setMetrics(PerformanceMonitor.getAllMetrics());
    }, 1000);

    return () => clearInterval(interval);
  }, [isVisible]);

  const handleClearCache = () => {
    PerformanceMonitor.clearMetrics();
    setRefreshKey(prev => prev + 1);
    console.log('Performance cache cleared');
  };

  const handleRefreshMetrics = () => {
    setMetrics(PerformanceMonitor.getAllMetrics());
  };

  if (!isVisible) {
    return (
      <button
        onClick={onToggle}
        className="fixed top-4 right-4 bg-blue-500 text-white px-3 py-1 rounded text-sm z-50 opacity-75 hover:opacity-100"
      >
        📊 Perf
      </button>
    );
  }

  const formatTime = (time: number) => `${time.toFixed(2)}ms`;
  const getPerformanceColor = (avgTime: number) => {
    if (avgTime < 16) return 'text-green-600';
    if (avgTime < 32) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="fixed top-4 right-4 bg-white border rounded-lg shadow-lg p-4 z-50 max-w-md">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-bold text-lg">Performance Dashboard</h3>
        <button onClick={onToggle} className="text-gray-500 hover:text-gray-700">✕</button>
      </div>
      
      <div className="mb-4 space-y-2">
        <button
          onClick={handleClearCache}
          className="bg-orange-500 text-white px-3 py-1 rounded text-sm mr-2"
        >
          Clear Cache
        </button>
        <button
          onClick={handleRefreshMetrics}
          className="bg-blue-500 text-white px-3 py-1 rounded text-sm"
        >
          Refresh
        </button>
      </div>

      <div className="space-y-2 max-h-80 overflow-y-auto">
        {Object.keys(metrics).length === 0 ? (
          <p className="text-gray-500 text-sm">No performance data yet...</p>
        ) : (
          Object.entries(metrics).map(([label, data]) => (
            <div key={label} className="border-b pb-2">
              <div className="flex justify-between items-center">
                <span className="font-medium text-sm">{label}</span>
                <span className={`text-sm ${getPerformanceColor(data.average)}`}>
                  {formatTime(data.average)}
                </span>
              </div>
              <div className="text-xs text-gray-500 flex justify-between">
                <span>Count: {data.count}</span>
                <span>Max: {formatTime(data.max)}</span>
              </div>
              {data.average > 16 && (
                <div className="text-xs text-red-500 mt-1">
                  ⚠️ Slower than 60fps target
                </div>
              )}
            </div>
          ))
        )}
      </div>
      
      <div className="mt-3 text-xs text-gray-500">
        <p>Target: &lt;16ms for 60fps</p>
        <p>Cache refresh key: {refreshKey}</p>
      </div>
    </div>
  );
};

export default PerformanceDashboard; 